import { useState } from "react";
import { ProgressBar } from "./ProgressBar";
import { buildDeepLinkUrl } from "../../services/theme-deeplink";
import { useSetupGuideStore } from "../../store/useSetupGuideStore";
import styles from "./SetupGuide.module.css";
import { useNavigate } from "react-router-dom";
import { StepAccordion } from "./StepAccordion";

interface SetupGuideProps {
  enabled: boolean | null;
}

export const SetupGuide = ({ enabled }: SetupGuideProps) => {
  const { step1Completed, step3Completed, toggleStep1, toggleStep3 } =
    useSetupGuideStore();
  const step2Completed = enabled === true;

  const [expandedStep, setExpandedStep] = useState<string | null>("step-1");

  const completedCount = [
    step1Completed,
    step2Completed,
    step3Completed,
  ].filter(Boolean).length;
  const progressPercent = (completedCount / 3) * 100;

  const toggleExpanded = (id: string) => {
    setExpandedStep(expandedStep === id ? null : id);
  };

  const navigate = useNavigate();

  return (
    <s-section heading="Guía de configuración">
      <s-box
        background="base"
        padding="large-200"
        borderRadius="large-100"
        border="small subdued solid"
      >
        <s-stack direction="block" gap="large-200">
          <s-text color="subdued">
            Usa esta guía personalizada para poner en marcha tu aplicación.
          </s-text>

          <s-stack direction="inline" gap="base" alignItems="center">
            <s-text>{completedCount} / 3 Completado</s-text>
            <ProgressBar percent={progressPercent} />
          </s-stack>

          <s-stack direction="block" gap="small-200">
            {/* Step 1: Comienza con Codway COD Form */}
            <StepAccordion
              id="step-1"
              title="Comienza con Codway COD Form"
              completed={step1Completed}
              expanded={expandedStep === "step-1"}
              onToggleExpand={() => toggleExpanded("step-1")}
              onToggleComplete={toggleStep1}
            >
              <s-text>
                <strong>Codway COD Form </strong>te permite sustituir el
                checkout tradicional de Shopify por un formulario de una sola
                página, totalmente personalizable y optimizado para pedidos de
                Pago Contra Entrega. Esta aplicación está pensada para darte
                control completo sobre cómo recibes tus pedidos COD, ayudándote
                a mejorar la tasa de conversión, reducir el fraude y aumentar
                tus ingresos mediante funciones avanzadas de upsell.
              </s-text>
              <s-text>
                Con <strong>Codway</strong>, tu formulario se integra
                directamente en las páginas de producto, ofreciendo una
                experiencia rápida y sencilla para el cliente. Además, puedes
                añadir campos personalizados, configurar tarifas de envío y
                mostrar ofertas de upsell para maximizar cada compra.
              </s-text>

              <s-button
                variant={step1Completed ? "secondary" : "primary"}
                onClick={() => toggleStep1()}
              >
                {step1Completed
                  ? "Marcar como pendiente"
                  : "Marcar como completado"}
              </s-button>
            </StepAccordion>

            {/* Step 2: Habilita la aplicación en tu tienda */}
            <StepAccordion
              id="step-2"
              title="Habilita la aplicación en tu tienda"
              completed={step2Completed}
              expanded={expandedStep === "step-2"}
              onToggleExpand={() => toggleExpanded("step-2")}
            >
              <s-text>
                Para utilizar el widget, necesitas habilitarlo desde el{" "}
                <a
                  href={buildDeepLinkUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.redirectLink}
                >
                  Editor de Tema
                </a>
                .
              </s-text>
            </StepAccordion>

            {/* Step 3 */}
            <StepAccordion
              id="step-3"
              title="Personaliza tu formulario"
              completed={step3Completed}
              expanded={expandedStep === "step-3"}
              onToggleExpand={() => toggleExpanded("step-3")}
              onToggleComplete={toggleStep3}
            >
              <s-text>
                Aquí puedes crear el formulario que reemplaza el checkout de
                Shopify para los pagos de COD. Con nuestro Diseñador de
                formularios, puedes personalizar completamente cómo se ve el
                formulario, qué campos recopilas y cómo se comporta en tus
                páginas de productos.
              </s-text>

              <s-button
                variant="primary"
                onClick={() => {
                  if (!step3Completed) toggleStep3();
                  navigate("/editor");
                }}
                icon="adjust"
              >
                Personalizar Formulario
              </s-button>
            </StepAccordion>
          </s-stack>
        </s-stack>
      </s-box>
    </s-section>
  );
};
